// ============================================================
// Stores API Service
// Connects to /api/v1/stores endpoints
// Falls back to mock if backend is unreachable
// ============================================================

import { httpClient, isApiReachable, ENABLE_MOCK_FALLBACK, ApiError } from "./http-client";

export interface Store {
  id: string;
  name: string;
  slug: string;
  owner_id: string;
  tier: string;
  /**
   * Label paket yang bisa dibaca manusia ("Business"), sumber `plans.name`.
   *
   * Opsional dengan alasan, bukan untuk menghindari komitmen: backend
   * memancarkannya `omitempty`, dan selama jendela sebelum SUBSC-33 tersaji
   * di sebuah lingkungan, ia memang belum dikirim. Yang benar saat ia tidak
   * ada bukan menebak — melainkan tidak menampilkan apa pun.
   */
  plan_name?: string;
  category: string;
  description: string;
  company_name?: string;
  status: string;
  trial_ends_at?: string;
  balance: number;
  monthly_budget?: number;
  team_count: number;
  max_team: number;
  ai_system_prompt?: string;
  ai_chat_temperature?: number;
  ai_chat_model?: string;
  ai_chat_max_words?: number;
  ai_chat_language?: string;
  ai_provider?: string;
  ai_handover_low_confidence?: boolean;
  ai_handover_on_request?: boolean;
  onboarding_status: string;
  onboarding_progress: Record<string, boolean>;
  onboarding_skipped: string[];
  onboarding_completed_at?: string;
  created_at: string;
  updated_at: string;
  /** Max KB groups (folders) for this store. null/undefined = unlimited */
  max_kb_groups?: number | null;
}

/** Single system prompt audit log entry (version history) */
export interface SystemPromptAuditEntry {
  id: string;
  store_id: string;
  changed_by_user_id?: string;
  changed_by_name?: string;
  changed_at: string;
  prompt_length_before: number;
  prompt_length_after: number;
  message?: string;
  is_autosave: boolean;
  /** Snippet (e.g. first 500 chars); used when prompt_after is empty (old entries) */
  prompt_snippet_after?: string;
  /** Full prompt for this version; load into editor when user clicks this version */
  prompt_after?: string;
}

/** Full AI config payload for PUT /api/v1/stores/:id/ai-prompt */
export interface UpdateAIConfigRequest {
  ai_system_prompt?: string;
  system_prompt?: string;
  temperature?: number;
  model?: string;
  max_words?: number;
  language?: string;
  provider?: string;
  handover_low_confidence?: boolean;
  handover_on_request?: boolean;
  /** Stored in system_prompt_audit_log.message when prompt changes */
  change_message?: string;
  /** true = autosave (skip vectoring to LLM); false/omit = manual save (run vectoring) */
  is_autosave?: boolean;
}

export interface CreateStoreRequest {
  name: string;
  tier?: string;
  category?: string;
  description?: string;
}

export interface UpdateStoreRequest {
  name?: string;
  category?: string;
  description?: string;
}

export interface UpdateAccountInfoRequest {
  store_id: string;
  company_name?: string;
  industry?: string;
  company_size?: string;
  timezone?: string;
}

/**
 * Get all stores for the authenticated user
 * GET /api/v1/stores
 */
export async function getMyStores(): Promise<Store[]> {
  try {
    const reachable = await isApiReachable();

    if (reachable) {
      const res = await httpClient.get<Store[]>("/api/v1/stores");
      if (res.success && res.data) {
        return res.data;
      }
    }

    if (ENABLE_MOCK_FALLBACK) return MOCK_STORES;
    throw new ApiError("API tidak tersedia", 0);
  } catch (error) {
    if (ENABLE_MOCK_FALLBACK) return MOCK_STORES;
    throw error;
  }
}

/**
 * Get a single store by ID
 * GET /api/v1/stores/:id
 */
export async function getStore(id: string): Promise<Store> {
  try {
    const reachable = await isApiReachable();

    if (reachable) {
      const res = await httpClient.get<Store>(`/api/v1/stores/${id}`);
      if (res.success && res.data) {
        return res.data;
      }
      throw new ApiError(res.error || "Store tidak ditemukan", 404);
    }

    if (ENABLE_MOCK_FALLBACK) {
      const store = MOCK_STORES.find((s) => s.id === id);
      if (store) return store;
      throw new ApiError("Store tidak ditemukan", 404);
    }
    throw new ApiError("API tidak tersedia", 0);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    if (ENABLE_MOCK_FALLBACK) {
      const store = MOCK_STORES.find((s) => s.id === id);
      if (store) return store;
    }
    throw new ApiError(error instanceof Error ? error.message : "Gagal memuat store", 0);
  }
}

/**
 * Create a new store
 * POST /api/v1/stores
 */
export async function createStore(data: CreateStoreRequest): Promise<Store> {
  try {
    const reachable = await isApiReachable();

    if (reachable) {
      const res = await httpClient.post<Store>("/api/v1/stores", data);
      if (res.success && res.data) {
        return res.data;
      }
      throw new ApiError(res.error || "Gagal membuat store", 500);
    }

    if (ENABLE_MOCK_FALLBACK) return mockCreateStore(data);
    throw new ApiError("API tidak tersedia", 0);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    if (ENABLE_MOCK_FALLBACK) return mockCreateStore(data);
    throw new ApiError(error instanceof Error ? error.message : "Gagal membuat store", 0);
  }
}

/**
 * Update a store's profile
 * PUT /api/v1/stores/:id
 */
export async function updateStore(id: string, data: UpdateStoreRequest): Promise<Store> {
  try {
    const reachable = await isApiReachable();

    if (reachable) {
      const res = await httpClient.put<Store>(`/api/v1/stores/${id}`, data);
      if (res.success && res.data) {
        return res.data;
      }
      throw new ApiError(res.error || "Gagal update store", 500);
    }

    if (ENABLE_MOCK_FALLBACK) return { ...MOCK_STORES[0], ...data, id };
    throw new ApiError("API tidak tersedia", 0);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    if (ENABLE_MOCK_FALLBACK) return { ...MOCK_STORES[0], ...data, id };
    throw new ApiError(error instanceof Error ? error.message : "Gagal update store", 0);
  }
}

/**
 * Get the caller's role in a specific store — a user's role can differ per
 * store (owner in one, cs_agent in another), so this must be refetched on
 * every store switch rather than reusing the role from login.
 * GET /api/v1/stores/:id/my-role
 */
export async function getMyRole(storeId: string): Promise<string> {
  const res = await httpClient.get<{ role: string }>(`/api/v1/stores/${storeId}/my-role`);
  if (res.success && res.data) return res.data.role;
  throw new ApiError(res.error || "Gagal memuat role", 404);
}

/**
 * Get system prompt audit log (version history) for a store
 * GET /api/v1/stores/:id/system-prompt-audit
 */
export async function getSystemPromptAudit(storeId: string): Promise<SystemPromptAuditEntry[]> {
  const res = await httpClient.get<SystemPromptAuditEntry[]>(`/api/v1/stores/${storeId}/system-prompt-audit`);
  if (res.success && Array.isArray(res.data)) return res.data;
  return [];
}

/**
 * Update AI system prompt only (legacy)
 * PUT /api/v1/stores/:id/ai-prompt
 */
export async function updateAIPrompt(id: string, prompt: string): Promise<void> {
  return updateAIConfig(id, { ai_system_prompt: prompt });
}

/**
 * Update full AI config for a store (prompt, temperature, model, language, handover, etc.)
 * PUT /api/v1/stores/:id/ai-prompt
 */
export async function updateAIConfig(id: string, data: UpdateAIConfigRequest): Promise<void> {
  try {
    const reachable = await isApiReachable();

    if (reachable) {
      const res = await httpClient.put(`/api/v1/stores/${id}/ai-prompt`, data);
      if (!res.success) {
        throw new ApiError(res.error || "Gagal update AI config", 500);
      }
      return;
    }

    if (ENABLE_MOCK_FALLBACK) return;
    throw new ApiError("API tidak tersedia", 0);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    if (ENABLE_MOCK_FALLBACK) return;
    throw new ApiError(error instanceof Error ? error.message : "Gagal update AI config", 0);
  }
}

/**
 * Update account/company info for a store (onboarding step 2)
 * PUT /api/v1/stores/:id/account-info
 *
 * Backend Status: ⚠️ NOT IMPLEMENTED YET
 * Will return 401/404 until backend implements this endpoint
 * See: docs/ONBOARDING_API_REQUIREMENTS.md
 */
export async function updateAccountInfo(data: UpdateAccountInfoRequest): Promise<void> {
  try {
    const reachable = await isApiReachable();

    if (reachable) {
      const res = await httpClient.put(`/api/v1/stores/${data.store_id}/account-info`, {
        company_name: data.company_name,
        industry: data.industry,
        company_size: data.company_size,
        timezone: data.timezone,
      });
      if (!res.success) {
        throw new ApiError(res.error || "Gagal update account info", 500);
      }
      console.log("[API] ✅ Account info saved successfully");
      return;
    }

    if (ENABLE_MOCK_FALLBACK) {
      console.log("[API] ⚠️ Mock mode: Account info not saved to database");
      return;
    }
    throw new ApiError("API tidak tersedia", 0);
  } catch (error) {
    // Gracefully handle 401/404 if endpoint not implemented yet
    if (error instanceof ApiError && (error.status === 401 || error.status === 404)) {
      console.warn("[API] ⚠️ Account info endpoint not implemented yet (returns", error.status, ")");
      console.warn("[API] 📝 See: docs/ONBOARDING_API_REQUIREMENTS.md for backend implementation");
      if (ENABLE_MOCK_FALLBACK) return; // Don't block user flow
      throw error;
    }

    if (error instanceof ApiError) throw error;
    if (ENABLE_MOCK_FALLBACK) {
      console.warn("[API] ⚠️ Account info save failed, continuing with mock fallback");
      return;
    }
    throw new ApiError(error instanceof Error ? error.message : "Gagal update account info", 0);
  }
}

// Mock helpers
function mockCreateStore(data: CreateStoreRequest): Store {
  return {
    id: `mock-store-${Date.now()}`,
    name: data.name,
    slug: data.name.toLowerCase().replace(/\s+/g, "-"),
    owner_id: "mock-owner",
    tier: data.tier || "starter",
    category: data.category || "",
    description: data.description || "",
    status: "active",
    balance: 0,
    team_count: 1,
    max_team: 3,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    onboarding_status: "pending",
    onboarding_progress: {},
    onboarding_skipped: [],
  };
}

const MOCK_STORES: Store[] = [
  {
    id: "store-1",
    name: "Toko Utama",
    slug: "toko-utama",
    owner_id: "user-1",
    tier: "business",
    category: "Elektronik",
    description: "Toko elektronik dan gadget terlengkap",
    status: "active",
    balance: 4500000,
    monthly_budget: 5000000,
    team_count: 8,
    max_team: 15,
    created_at: "2026-01-01T00:00:00Z",
    updated_at: "2026-02-15T00:00:00Z",
    onboarding_status: "pending",
    onboarding_progress: {},
    onboarding_skipped: [],
  },
  {
    id: "store-2",
    name: "Toko Cabang Jakarta",
    slug: "toko-cabang-jakarta",
    owner_id: "user-1",
    tier: "starter",
    category: "Elektronik",
    description: "Cabang Jakarta untuk produk elektronik",
    status: "active",
    balance: 1200000,
    monthly_budget: 2000000,
    team_count: 3,
    max_team: 3,
    created_at: "2026-01-15T00:00:00Z",
    updated_at: "2026-02-15T00:00:00Z",
    onboarding_status: "pending",
    onboarding_progress: {},
    onboarding_skipped: [],
  },
  {
    id: "store-3",
    name: "Toko Cabang Surabaya",
    slug: "toko-cabang-surabaya",
    owner_id: "user-1",
    tier: "starter",
    category: "Fashion",
    description: "Cabang Surabaya untuk produk fashion",
    status: "trial",
    balance: 500000,
    team_count: 2,
    max_team: 3,
    created_at: "2026-02-01T00:00:00Z",
    updated_at: "2026-02-15T00:00:00Z",
    onboarding_status: "pending",
    onboarding_progress: {},
    onboarding_skipped: [],
  },
];
